<script setup>
import { f7 } from 'framework7-vue';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import PoolaProgressBar from './PoolaProgressBar.vue';
import PoolaShareAction from './PoolaShareAction.vue';
import { useCurrency } from '@/composables/useCurrency.js';
import { useOffscreenAnimationGate } from '@/composables/useOffscreenAnimationGate.js';
import { fireRewardBurst, prefersReducedMotion } from '@/utils/confetti.js';

const props = defineProps({
  offer: {
    type: Object,
    required: true,
  },
  phase: {
    type: String,
    required: true,
    validator: (v) => ['scheduled', 'active', 'ended'].includes(v),
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  // Prospect (non-member) read-only mode: in place of the suppressed CTA row,
  // render a «للأعضاء بس» lock tag telling the prospect that claiming is
  // members-only (BE forces canClaim=false for a prospect viewer). Suppliers
  // pass read-only WITHOUT this, so their cards stay tag-less.
  membersOnlyTag: {
    type: Boolean,
    default: false,
  },
  // Deal/festival "last hours" urgency window — computed upstream (PoolaRun /
  // the festival urgency clock), NOT recomputed here. Defaults to false until
  // a host wires the real signal through.
  finalHours: {
    type: Boolean,
    default: false,
  },
  // Cap-unlock countdown (P2-A). The ONE deal-wide clock ticking to the
  // scheduled per-member cap lift (`deal.capIncreaseAt`), driven in PoolaRun and
  // shared by every card. Reactive `{ days, hours, minutes, seconds, isPast }`
  // (useCountdown state) or null when no lift is pending. Duck-typed so a plain
  // fixture works. `capUnlockPending` is the boolean "a lift is scheduled and
  // still in the future for this run" — kept separate so the per-card predicate
  // (showCapCountdown) can gate on the deal-wide fact without re-deriving it.
  capUnlock: {
    type: Object,
    default: null,
  },
  capUnlockPending: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['claim']);
const { t } = useI18n();
const { formatCurrency } = useCurrency();

// Duck-typed on `offer` so both a real PoolaOffer model instance (getters) and
// a plain test fixture (data props) work the same way.
const isComplete = computed(() => !!props.offer.isComplete);
const isNearComplete = computed(() => !!props.offer.isNearComplete);
const hasRewardAmount = computed(() => props.offer.myPoolcreditEarnedCents != null);

// Collective بولكريديت returned when the عرض completes = the total بولشير every
// claim burned, all of which comes back to the members as بولكريديت. Derivable
// client-side (poolshareCents is per-unit; 1 PoolShare cent = 1 EGP cent), so
// the celebration amount needs NO backend field — unlike the per-user
// myPoolcreditEarnedCents. null when not complete / no poolshare / no claims.
const collectivePoolcreditCents = computed(() => {
  if (!isComplete.value) return null;
  const { poolshareCents, piecesClaimed } = props.offer;
  if (!(poolshareCents > 0) || !(piecesClaimed > 0)) return null;
  return poolshareCents * piecesClaimed;
});

const discountPct = computed(() => Math.round(props.offer.discountBp / 100));

// Split bar flex ratios — dir="ltr" on the bar wrapper keeps cash-left,
// poolshare-right as a data visualisation in all locales (RTL included).
const splitTotal = computed(() => props.offer.cashCents + props.offer.poolshareCents);
const cashFlex = computed(() =>
  splitTotal.value > 0 ? props.offer.cashCents / splitTotal.value : 0.5,
);
const poolshareFlex = computed(() =>
  splitTotal.value > 0 ? props.offer.poolshareCents / splitTotal.value : 0.5,
);

// Low-stock: show urgency tag when remaining ≤ max(5, 10% of total)
const isLowStock = computed(() => {
  const { piecesRemaining, piecesTotal } = props.offer;
  return piecesRemaining > 0 && piecesRemaining <= Math.max(5, Math.floor(piecesTotal * 0.1));
});

const hasSupplier = computed(() => {
  const n = props.offer.supplierName;
  return n != null && String(n).trim() !== '';
});

const isSoldOut = computed(() => props.offer.piecesRemaining === 0);
const isActive = computed(() => props.phase === 'active');

const isCapReached = computed(
  () =>
    props.offer.maxPerMember != null && (props.offer.myClaimedQty ?? 0) >= props.offer.maxPerMember,
);

// ── Cap-unlock countdown (P2-A) ──────────────────────────────────────────────
// Show the "buy N more in HH:MM:SS" countdown on THIS card iff the member is
// disabled BECAUSE of the per-member cap AND a scheduled lift will free ≥1 piece
// for them (the brief's exact predicate — 2026-07-13 cap-increase). Active phase
// only. `canClaim` is a single boolean and never says WHY it's false, so the cap
// reason is derived here from myClaimedQty >= maxPerMember; a sold-out or
// balance-blocked card fails an earlier clause and shows its normal state.
const showCapCountdown = computed(() => {
  if (!props.capUnlockPending || !isActive.value) return false;
  const cu = props.capUnlock;
  if (!cu || cu.isPast) return false;
  const { maxPerMember, upcomingMaxPerMember } = props.offer;
  if (maxPerMember == null || upcomingMaxPerMember == null) return false;
  if (upcomingMaxPerMember <= maxPerMember) return false; // the bump must raise THIS offer
  const held = props.offer.myClaimedQty ?? 0;
  return held >= maxPerMember && held < upcomingMaxPerMember;
});

// Pieces the lift frees for this member (normally === capIncreaseDelta) — drives
// the "buy {headroom} more" copy count.
const capHeadroom = computed(
  () => (props.offer.upcomingMaxPerMember ?? 0) - (props.offer.myClaimedQty ?? 0),
);

// HH:MM:SS (days prefix only when > 0). String()/padStart guarantees Western
// digits regardless of locale (never Arabic-Indic — owner-locked UI rule); the
// time cell uses tabular-nums so the digits don't jitter as they tick.
function pad2(n) {
  return String(n).padStart(2, '0');
}
const capCountdownText = computed(() => {
  const cu = props.capUnlock;
  if (!cu) return '';
  const core = `${pad2(cu.hours)}:${pad2(cu.minutes)}:${pad2(cu.seconds)}`;
  return cu.days > 0 ? `${cu.days}:${core}` : core;
});

// Final-hours + near-complete skin — active phase only (finalHours is only
// ever true while a run is live).
const showFinalHoursTag = computed(
  () => isActive.value && props.finalHours && isNearComplete.value,
);

const ctaLabel = computed(() => {
  if (props.phase === 'scheduled') return t('poola.buy.card.startsSoon');
  if (isSoldOut.value) return t('poola.buy.card.soldOut');
  if (isCapReached.value) return t('poola.buy.why.capReached');
  if (props.phase === 'ended') return t('poola.buy.why.ended');
  if (props.finalHours && isNearComplete.value) return t('poola.buy.card.ctaFinish');
  return t('poola.buy.card.ctaActive');
});

const ctaEnabled = computed(() => isActive.value && !isSoldOut.value && !isCapReached.value);

function onClaim() {
  if (!ctaEnabled.value) return;
  // Guard the completion celebration against firing on the member's OWN
  // claim (see suppressOwnCompletion below) — armed here, synchronously,
  // before the parent's async claim() has a chance to patch the offer.
  armOwnClaimSuppression();
  emit('claim', props.offer);
}

function showCompleteExplainer() {
  f7.dialog.alert(t('poola.buy.card.completeExplainer'), t('poola.buy.card.completePromise'));
}

// ── Completion celebration (P3-A) ────────────────────────────────────────
// A one-time confetti burst + haptic + banner when THIS offer is observed
// completing (piecesRemaining → 0) while the card is on-screen, plus a
// small looping 🎁 sticker on the completed card corner. Grammar copied from
// PoolaFestivalOpening.vue: fire once, never on the viewer's own action,
// reduced-motion → static fallback only.
const reduced = ref(prefersReducedMotion());
const { el: mediaEl, isVisible: cardVisible } = useOffscreenAnimationGate();

// Deterministic sparkle field for the always-on completed-card celebration —
// static positions/delays/sizes (no randomness) so it stays SSR/test-stable.
// Spread across the top band + side edges so the whole card reads celebratory
// without burying the product centre.
const CELEBRATE_SPARKLES = [
  { left: 6, top: 14, size: 17, delay: 0, dur: 3.2 },
  { left: 16, top: 34, size: 11, delay: 1.3, dur: 3.9 },
  { left: 30, top: 9, size: 14, delay: 0.6, dur: 3.4 },
  { left: 12, top: 55, size: 10, delay: 2.2, dur: 4.1 },
  { left: 90, top: 16, size: 18, delay: 0.3, dur: 3.1 },
  { left: 80, top: 36, size: 12, delay: 1.5, dur: 3.7 },
  { left: 94, top: 52, size: 10, delay: 2.0, dur: 4.0 },
  { left: 68, top: 10, size: 13, delay: 0.9, dur: 3.5 },
  { left: 46, top: 20, size: 12, delay: 1.8, dur: 3.8 },
  { left: 58, top: 40, size: 11, delay: 2.5, dur: 4.2 },
];

// Festival-Combo confetti rain — deterministic (SSR/test-stable) festival-colored
// pieces falling inside the completed card's media. Fixed positions/colors/durations
// and NEGATIVE delays (pre-seed the loop mid-fall so the card is already "raining" on
// first paint, never an empty→fill ramp). CSS-only transform/opacity; colors mirror
// FESTIVAL_COLORS in confetti.js. Paused off-screen + suppressed under reduced motion.
const CELEBRATE_CONFETTI = [
  { left: 8, w: 7, color: '#fbbf24', delay: -0.2, dur: 3.1 },
  { left: 18, w: 6, color: '#a78bfa', delay: -1.4, dur: 3.8 },
  { left: 27, w: 8, color: '#f472b6', delay: -0.7, dur: 2.9 },
  { left: 36, w: 6, color: '#34d399', delay: -2.1, dur: 4.0 },
  { left: 45, w: 7, color: '#60a5fa', delay: -1.0, dur: 3.3 },
  { left: 54, w: 6, color: '#ffffff', delay: -2.6, dur: 3.6 },
  { left: 62, w: 8, color: '#fbbf24', delay: -0.4, dur: 3.0 },
  { left: 70, w: 6, color: '#a78bfa', delay: -1.7, dur: 3.9 },
  { left: 78, w: 7, color: '#f472b6', delay: -0.9, dur: 3.2 },
  { left: 86, w: 6, color: '#34d399', delay: -2.3, dur: 4.1 },
  { left: 93, w: 7, color: '#60a5fa', delay: -1.2, dur: 3.4 },
  { left: 13, w: 6, color: '#ffffff', delay: -3.0, dur: 3.7 },
  { left: 50, w: 8, color: '#fbbf24', delay: -1.9, dur: 2.8 },
  { left: 33, w: 6, color: '#a78bfa', delay: -0.5, dur: 3.5 },
];

const CELEBRATE_BANNER_MS = 3200;
// Bounds how long an own-claim can suppress a LATER, unrelated completion
// (e.g. this claim didn't sell out the offer, but someone else's does
// moments later) — generous enough to cover the optimistic patch or a
// realtime round-trip, short enough not to eat a genuine next completion.
const OWN_CLAIM_SUPPRESSION_MS = 5000;

const hasCelebrated = ref(false);
const showCelebrateBanner = ref(false);
// Element ref on the «مكتمل ✓» badge — the coin fountain anchors to its rect so
// the burst reads as THIS card completing, not a viewport-wide sprinkle.
const completeBadgeEl = ref(null);
// The amount shown in the banner, animated 0 → collective total for the count-up.
const rewardDisplayCents = ref(0);
let suppressOwnCompletion = false;
let suppressionTimer = null;
let bannerTimer = null;
let countupRaf = null;

function armOwnClaimSuppression() {
  suppressOwnCompletion = true;
  if (suppressionTimer) window.clearTimeout(suppressionTimer);
  suppressionTimer = window.setTimeout(() => {
    suppressOwnCompletion = false;
  }, OWN_CLAIM_SUPPRESSION_MS);
}

// Count the collective total up from zero for the banner. Deterministic under
// reduced motion / no rAF (jumps to the final value); animates otherwise.
function startRewardCountup() {
  const target = collectivePoolcreditCents.value;
  if (target == null) {
    rewardDisplayCents.value = 0;
    return;
  }
  if (reduced.value || typeof requestAnimationFrame !== 'function') {
    rewardDisplayCents.value = target;
    return;
  }
  if (countupRaf) cancelAnimationFrame(countupRaf);
  rewardDisplayCents.value = 0;
  const now = () =>
    typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  const startAt = now();
  const DURATION_MS = 1000;
  const tick = () => {
    const k = Math.min(1, (now() - startAt) / DURATION_MS);
    const eased = 1 - Math.pow(1 - k, 3);
    rewardDisplayCents.value = Math.round(target * eased);
    if (k < 1) {
      countupRaf = requestAnimationFrame(tick);
    } else {
      rewardDisplayCents.value = target;
      countupRaf = null;
    }
  };
  countupRaf = requestAnimationFrame(tick);
}

function celebrate() {
  hasCelebrated.value = true;
  if (!reduced.value) {
    // Fired from a flush:'post' watcher, so the v-if badge is already in the
    // DOM — anchor the coin fountain to it (fall back to the media box).
    fireRewardBurst(completeBadgeEl.value || mediaEl.value || null);
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      try {
        navigator.vibrate([12, 40, 12]);
      } catch {
        // haptic is best-effort; ignore unsupported/blocked
      }
    }
  }
  showCelebrateBanner.value = true;
  startRewardCountup();
  if (bannerTimer) window.clearTimeout(bannerTimer);
  bannerTimer = window.setTimeout(() => {
    showCelebrateBanner.value = false;
  }, CELEBRATE_BANNER_MS);
}

// Tracks the fields a claim (mine or another member's) actually moves, so the
// watcher below can tell "the patch resulting from MY claim just landed"
// apart from "nothing happened yet" — piecesRemaining alone isn't enough
// once realtime is connected (see the class-level comment on claim()).
const claimSignature = computed(
  () => `${props.offer.piecesRemaining}:${props.offer.myClaimedQty ?? 0}`,
);

// No `immediate` — a card that mounts already complete (scrolled to later,
// historical offer) must NOT retro-celebrate; only a genuine flip does.
// Own-claim suppression is consumed by the FIRST patch observed after the
// claim (armOwnClaimSuppression), whether or not that patch completes the
// offer — so a claim that doesn't sell out the offer stops suppressing in
// time for a later, genuinely-someone-else's completion to celebrate.
watch(
  [isComplete, claimSignature],
  ([val, sig], [prevVal, prevSig]) => {
    if (sig === prevSig && val === prevVal) return;
    const wasSuppressed = suppressOwnCompletion;
    if (wasSuppressed) {
      suppressOwnCompletion = false;
      if (suppressionTimer) window.clearTimeout(suppressionTimer);
    }
    if (!val || prevVal || hasCelebrated.value) return;
    if (wasSuppressed) {
      hasCelebrated.value = true; // still "seen" — never celebrate this offer retroactively
      return;
    }
    if (!cardVisible.value) return;
    celebrate();
  },
  { flush: 'post' },
); // 'post' so the v-if «مكتمل ✓» badge is mounted before celebrate() anchors the burst to it

onBeforeUnmount(() => {
  if (suppressionTimer) window.clearTimeout(suppressionTimer);
  if (bannerTimer) window.clearTimeout(bannerTimer);
  if (countupRaf) cancelAnimationFrame(countupRaf);
});
</script>

<template>
  <f7-card
    class="poola-offer-card"
    :class="{
      'poola-offer-card--complete': isComplete,
      'poola-offer-card--paused': isComplete && !cardVisible,
    }"
  >
    <!-- ── Media (edge-to-edge) ────────────────────────────────────────── -->
    <div
      ref="mediaEl"
      class="offer-media"
      :class="{ 'offer-media--scheduled': phase === 'scheduled' }"
    >
      <img
        v-if="offer.photoUrl"
        :src="offer.photoUrl"
        :alt="offer.displayTitle"
        class="offer-media__img"
        loading="lazy"
        @error="(e) => e.target.classList.add('offer-media__img--hidden')"
      />
      <div v-else class="offer-media__placeholder">
        <f7-icon ios="f7:bag_fill" md="material:shopping_bag" size="40" color="white"></f7-icon>
      </div>

      <!-- Scheduled dim + "معاينة" lock overlay -->
      <div v-if="phase === 'scheduled'" class="offer-media__lock">
        <span class="offer-media__preview-label">{{ t('poola.buy.card.preview') }}</span>
      </div>

      <!-- Always-on looping celebration — a gentle stream of gold coins rising
           near the «مكتمل ✓» badge on EVERY completed card (not just the one
           that just flipped). CSS-only (transform/opacity), so a whole list of
           completed cards stays cheap; paused off-screen via the shared gate,
           hidden under reduced-motion (static badge/skin carry it). Placed
           before the badge so the badge paints on top. -->
      <div
        v-if="isComplete"
        class="offer-celebrate-loop"
        :class="{ 'offer-celebrate-loop--paused': !cardVisible }"
        aria-hidden="true"
      >
        <i
          v-for="(s, idx) in CELEBRATE_SPARKLES"
          :key="idx"
          class="offer-celebrate-spark"
          :style="{
            left: s.left + '%',
            top: s.top + '%',
            '--sz': s.size + 'px',
            '--delay': s.delay + 's',
            '--dur': s.dur + 's',
          }"
        />
      </div>

      <!-- Festival Combo (P3-A) — soft gold glow pulse behind the confetti. Opacity
           only (a static radial-gradient wash whose opacity breathes), so it reads
           as a warm pulsing light with NO paint-triggering box-shadow animation. -->
      <div
        v-if="isComplete"
        class="offer-celebrate-glow"
        :class="{ 'offer-celebrate-glow--paused': !cardVisible }"
        aria-hidden="true"
      />

      <!-- Festival Combo — continuous festival confetti rain inside the media.
           Deterministic pieces (CELEBRATE_CONFETTI), transform/opacity only,
           paused off-screen via the shared gate, hidden under reduced motion. -->
      <div
        v-if="isComplete"
        class="offer-celebrate-confetti"
        :class="{ 'offer-celebrate-confetti--paused': !cardVisible }"
        aria-hidden="true"
      >
        <i
          v-for="(c, idx) in CELEBRATE_CONFETTI"
          :key="idx"
          class="offer-celebrate-confetti-piece"
          :style="{
            left: c.left + '%',
            background: c.color,
            '--w': c.w + 'px',
            '--delay': c.delay + 's',
            '--dur': c.dur + 's',
          }"
        />
      </div>

      <!-- Festival Combo — a light sheen sweeping across the media on loop, the
           "premium winner" gloss. Transform only, pausable off-screen. -->
      <div
        v-if="isComplete"
        class="offer-celebrate-sheen"
        :class="{ 'offer-celebrate-sheen--paused': !cardVisible }"
        aria-hidden="true"
      >
        <span class="offer-celebrate-sheen-bar" />
      </div>

      <!-- «بولشير {pct}%» seal — top-start (right in RTL via inset-inline-start) -->
      <span class="offer-overlay offer-overlay--seal">
        {{ t('poola.buy.card.shareBadge', { pct: discountPct }) }}
      </span>

      <!-- Completion / final-hours / low-stock urgency tag — top-end, mutually
           exclusive (a completed offer never shows low-stock, etc). -->
      <span
        v-if="isComplete"
        ref="completeBadgeEl"
        class="offer-overlay offer-overlay--urgency offer-overlay--complete"
      >
        {{ t('poola.buy.card.completedBadge') }}
      </span>
      <span
        v-else-if="showFinalHoursTag"
        class="offer-overlay offer-overlay--urgency offer-overlay--urgency-final"
      >
        {{ t('poola.buy.card.finalHoursTag') }}
      </span>
      <span v-else-if="isLowStock" class="offer-overlay offer-overlay--urgency">
        {{ t('poola.buy.card.lowStock', { n: offer.piecesRemaining }) }}
      </span>

      <!-- «مِنك» badge — bottom-end; visible to suppliers on their own items -->
      <span v-if="offer.isMine" class="offer-overlay offer-overlay--mine">
        {{ t('poola.supplier.mineBadge') }}
      </span>

      <!-- Supplier chip — bottom-start; hidden when supplierName is null (STANDALONE) -->
      <div v-if="hasSupplier" class="offer-overlay offer-overlay--supplier">
        <div v-if="offer.supplierAvatarUrl" class="supplier-chip__avatar-wrap">
          <img
            :src="offer.supplierAvatarUrl"
            :alt="offer.supplierName"
            class="supplier-chip__avatar"
            loading="lazy"
            @error="(e) => (e.target.style.display = 'none')"
          />
        </div>
        <span class="supplier-chip__name">{{ offer.supplierName }}</span>
        <f7-icon
          v-if="offer.supplierIsPlatform"
          ios="f7:checkmark_seal_fill"
          md="material:verified"
          size="13"
          color="blue"
          class="supplier-chip__vetted"
        ></f7-icon>
      </div>

      <!-- Looping 🎁 sticker — completed cards only; CSS-only bob + glow,
           paused off-screen via the shared IntersectionObserver gate
           (cardVisible) so a long completed-offers list never runs dozens of
           concurrent animations. Static under reduced-motion (media query in
           the non-scoped style block below). -->
      <span
        v-if="isComplete"
        class="offer-celebrate-sticker"
        :class="{ 'offer-celebrate-sticker--paused': !cardVisible }"
        aria-hidden="true"
      >🎁</span>

      <!-- One-time completion banner — fires only on an observed flip into
           complete (never the viewer's own claim); auto-dismisses. -->
      <div
        v-if="showCelebrateBanner"
        class="offer-celebrate-banner"
        :data-reduced="reduced"
        role="status"
        aria-live="polite"
      >
        <span class="offer-celebrate-banner__title">{{ t('poola.buy.card.celebrateTitle') }}</span>
        <span v-if="collectivePoolcreditCents != null" class="offer-celebrate-banner__reward">{{
          t('poola.buy.card.celebrateRewardCollective', {
            amount: formatCurrency(rewardDisplayCents),
          })
        }}</span>
      </div>
    </div>

    <!-- ── Card content ────────────────────────────────────────────────── -->
    <f7-card-content>
      <!-- Title -->
      <h3 class="offer-title">{{ offer.displayTitle }}</h3>

      <!-- Catalog line (null → block hidden; STANDALONE omit) -->
      <p v-if="offer.productName" class="offer-catalog">
        {{ offer.productName
        }}<template v-if="offer.variantName"> · {{ offer.variantName }}</template>
      </p>

      <!-- Flavor text (sanitized by backend; truncated; STANDALONE null → omit) -->
      <p v-if="offer.postContent" class="offer-flavor">{{ offer.postContent }}</p>

      <div class="price-split">
        <!-- Cash hero: the cash the member actually pays is the big number; the
             full retail is a struck reference ("instead of 115"); the
             PoolShare-covered amount is the members' benefit. NOT a discount —
             the gap is paid by the member's own earned PoolShare. -->
        <p class="price-cash__label">{{ t('poola.buy.card.payCashLabel') }}</p>
        <p class="price-cash__hero">
          {{ formatCurrency(offer.cashCents) }}
          <span class="price-cash__retail">
            {{ t('poola.buy.card.insteadOf') }} <s>{{ formatCurrency(offer.unitPriceCents) }}</s>
          </span>
        </p>
        <p class="price-cash__covers">
          {{
            t('poola.buy.card.poolshareCovers', { amount: formatCurrency(offer.poolshareCents) })
          }}
        </p>
        <!-- dir="ltr" keeps cash-left/poolshare-right regardless of page direction -->
        <div class="price-split__bar" dir="ltr">
          <div
            class="price-split__segment price-split__segment--cash"
            :style="{ flex: cashFlex }"
          />
          <div
            class="price-split__segment price-split__segment--poolshare"
            :style="{ flex: poolshareFlex }"
          />
        </div>
        <p class="price-split__legend">
          {{ t('poola.buy.card.cashLabel') }} {{ formatCurrency(offer.cashCents) }} ·
          {{ t('poola.buy.card.poolshareLabel') }} {{ formatCurrency(offer.poolshareCents) }}
        </p>
      </div>

      <!-- Progress + social proof -->
      <div class="offer-progress">
        <poola-progress-bar
          :pieces-claimed="offer.piecesClaimed"
          :pieces-total="offer.piecesTotal"
          :complete="isComplete"
        ></poola-progress-bar>
        <p class="offer-progress__text">
          <template v-if="isActive && isNearComplete">{{
            t('poola.buy.card.goalRemaining', { n: offer.piecesRemaining })
          }}</template>
          <template v-else>{{ t('poola.buy.card.claimed', { n: offer.piecesClaimed }) }} ·
            {{ t('poola.buy.card.remaining', { n: offer.piecesRemaining }) }}</template>
        </p>
      </div>

      <!-- Complete-promise pill — active + near-complete only; tap-to-learn
           opens an explainer dialog rather than cramming the full copy on the card. -->
      <button
        v-if="isActive && isNearComplete"
        type="button"
        class="offer-cap-chip offer-promise-chip"
        @click="showCompleteExplainer"
      >
        {{ t('poola.buy.card.completePromise') }}
      </button>

      <!-- Per-member cap chip (omit when null = uncapped) -->
      <span v-if="offer.maxPerMember != null" class="offer-cap-chip">
        {{ t('poola.buy.card.perMemberCap', { n: offer.maxPerMember }) }}
      </span>

      <!-- Share action — PRIMARY CTA in scheduled (claim is locked; share is the
           pre-market action). Placed BEFORE the disabled claim button so it is
           the lead element. Hidden when ended or when offer has no linked post. -->
      <poola-share-action
        v-if="offer.hasShareablePost && phase === 'scheduled'"
        :offer="offer"
        class="offer-share offer-share--primary"
      ></poola-share-action>

      <!-- Completed state — replaces the CTA row entirely (claiming is over).
           A claimant with a granted reward sees the amount; a claimant whose
           reward hasn't landed yet (BE field not shipped) sees the completed
           skin with no amount line (never a placeholder); a non-claimant sees
           the social line. -->
      <div v-if="isComplete" class="offer-completed-row">
        <span v-if="offer.myClaimedQty > 0 && hasRewardAmount" class="offer-reward-chip">{{
          t('poola.buy.card.rewardChip', { amount: formatCurrency(offer.myPoolcreditEarnedCents) })
        }}</span>
        <!-- Collective line — everyone who didn't get a per-user amount yet
             (non-claimants, and claimants awaiting the BE reward field) sees the
             derivable collective total, never a placeholder. -->
        <span
          v-else-if="collectivePoolcreditCents != null"
          class="offer-reward-chip offer-reward-chip--social"
        >{{
          t('poola.buy.card.completedSocial', {
            amount: formatCurrency(collectivePoolcreditCents),
          })
        }}</span>
      </div>

      <!-- CTA row — suppressed entirely in read-only mode (suppliers never claim) -->
      <div v-else-if="!readOnly" class="offer-cta-row">
        <f7-button
          fill
          large
          class="offer-cta-btn"
          :class="{ 'offer-cta-btn--cap-unlock': showCapCountdown }"
          :disabled="!ctaEnabled"
          @click="onClaim"
        >
          <span v-if="showCapCountdown" class="cap-unlock-cta">
            <span class="cap-unlock-cta__line1">{{
              t('poola.buy.card.capUnlock.line1', { headroom: capHeadroom })
            }}</span>
            <span class="cap-unlock-cta__time">{{ capCountdownText }}</span>
          </span>
          <template v-else>{{ ctaLabel }}</template>
        </f7-button>

        <!-- Source link only when telegramPostUrl is non-null -->
        <f7-link
          v-if="offer.telegramPostUrl"
          :href="offer.telegramPostUrl"
          external
          target="_system"
          class="offer-source-link"
        >{{ t('poola.buy.card.viewSource') }}</f7-link>
      </div>

      <!-- Members-only tag — prospect read-only mode only. The guard is explicit
           (readOnly && membersOnlyTag), not merely positional, so intent matches
           behavior and it can never leak to a seller. Sits where the claim CTA
           would be: claiming is a members-only perk. A read-only SUPPLIER passes
           read-only WITHOUT members-only-tag, so this stays hidden for them. -->
      <div v-else-if="readOnly && membersOnlyTag" class="offer-members-only" role="note">
        <f7-icon
          ios="f7:lock_fill"
          md="material:lock"
          size="15"
          class="offer-members-only__icon"
        ></f7-icon>
        <span>{{ t('poola.buy.prospect.cardMembersOnlyTag') }}</span>
      </div>

      <!-- Share action — SECONDARY in active (claim is the primary CTA above;
           share sits below as a supplementary action). -->
      <poola-share-action
        v-if="offer.hasShareablePost && phase === 'active'"
        :offer="offer"
        class="offer-share offer-share--secondary"
      ></poola-share-action>
    </f7-card-content>
  </f7-card>
</template>

<!-- scoped: component-local rules -->
<style scoped>
.poola-offer-card {
  margin: var(--poolit-spacing-md, 16px);
  border-radius: var(--poolit-radius-lg, 16px);
  overflow: hidden;
}

/* Completed skin — gold border + soft wash. Symmetric (no direction-dependent
   values), so it needs no RTL override. */
.poola-offer-card--complete {
  border: 2px solid #d4af37;
  background: linear-gradient(180deg, rgba(212, 175, 55, 0.1), transparent 45%);
}

/* ── Media ──────────────────────────────────────────────────────────────── */
.offer-media {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: var(--poolit-primary-900, #3b0764);
  overflow: hidden;
}

.offer-media__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.offer-media__img--hidden {
  display: none !important;
}

.offer-media__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--poolit-primary-700, #6d28d9),
    var(--poolit-primary-900, #3b0764)
  );
}

/* Scheduled dim overlay */
.offer-media--scheduled .offer-media__img,
.offer-media--scheduled .offer-media__placeholder {
  filter: brightness(0.45);
}

.offer-media__lock {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.offer-media__preview-label {
  padding: 6px 18px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: var(--poolit-radius-full);
  color: #fff;
  font-size: var(--poolit-font-size-body, 0.9375rem);
  font-weight: var(--poolit-font-weight-semibold);
  backdrop-filter: blur(4px);
}

/* ── Overlays (CSS logical props handle RTL automatically) ───────────────── */
.offer-overlay {
  position: absolute;
  padding: 4px 10px;
  border-radius: var(--poolit-radius-full);
  font-size: 12px;
  font-weight: var(--poolit-font-weight-bold, 700);
  line-height: 1.3;
  white-space: nowrap;
}

.offer-overlay--seal {
  inset-inline-start: var(--poolit-spacing-sm, 8px);
  top: var(--poolit-spacing-sm, 8px);
  background: var(--poolit-primary-600, #7c3aed);
  color: #fff;
}

.offer-overlay--urgency {
  inset-inline-end: var(--poolit-spacing-sm, 8px);
  top: var(--poolit-spacing-sm, 8px);
  background: #f59e0b;
  color: #fff;
}

/* Final-hours + near-complete — gold, one notch hotter than the plain amber urgency tag */
.offer-overlay--urgency-final {
  background: linear-gradient(135deg, #f59e0b, #d4af37);
  color: #1f2937;
}

/* Completed — solid gold, distinct from the amber urgency family. Mounts
   fresh only when isComplete flips true (v-if), so the "stamp" keyframe
   plays exactly once, naturally, on mount — no JS trigger needed. */
.offer-overlay--complete {
  background: linear-gradient(135deg, #d4af37, #b8860b);
  color: #fff;
  transform-origin: center;
  /* Stamp once on mount, THEN pulse forever (delay > stamp duration so the two
     transform animations never overlap). Pulse pauses off-screen via --paused. */
  animation:
    offer-badge-stamp 0.42s cubic-bezier(0.34, 1.56, 0.64, 1),
    offer-badge-pulse 1.9s ease-in-out 0.6s infinite;
}

.poola-offer-card--paused .offer-overlay--complete {
  animation-play-state: paused;
}

@keyframes offer-badge-stamp {
  from {
    transform: scale(0.6);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes offer-badge-pulse {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.09);
  }
}

@media (prefers-reduced-motion: reduce) {
  .offer-overlay--complete {
    animation: none;
  }
}

/* ── Completion sticker (P3-A) — contained 🎁 bob + glow, transform/opacity
   only. `--paused` (off-screen, via the shared IntersectionObserver gate)
   just toggles animation-play-state; no JS animation loop involved. ────── */
.offer-celebrate-sticker {
  position: absolute;
  inset-inline-end: var(--poolit-spacing-sm, 8px);
  bottom: var(--poolit-spacing-sm, 8px);
  width: 40px;
  height: 40px;
  max-width: 56px;
  max-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  line-height: 1;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(212, 175, 55, 0.35), rgba(212, 175, 55, 0) 70%);
  animation:
    offer-sticker-bob 2.4s ease-in-out infinite,
    offer-sticker-glow 2.4s ease-in-out infinite;
  will-change: transform, opacity;
  pointer-events: none;
}

/* Same-side «مِنك» badge and completion sticker never coexist (mine is
   supplier-only; the sticker is member-surface-only), so no offset needed. */

.offer-celebrate-sticker--paused {
  animation-play-state: paused;
}

@keyframes offer-sticker-bob {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-5px);
  }
}

@keyframes offer-sticker-glow {
  0%,
  100% {
    opacity: 0.75;
  }

  50% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .offer-celebrate-sticker {
    animation: none;
    opacity: 0.9;
  }
}

/* ── Always-on looping celebration — a field of twinkling gold sparkles across
   the completed card's media. Bright gold core + gold glow + a faint dark edge
   so they read over ANY product photo (light or dark). transform/opacity only
   (GPU), staggered per-sparkle, pausable off-screen (animation-play-state),
   hidden under reduced-motion. Above the media image, below the overlay badges
   (DOM order). ─────────────────────────────────────────────────────────────── */
.offer-celebrate-loop {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 4;
}

.offer-celebrate-spark {
  position: absolute;
  width: var(--sz, 12px);
  height: var(--sz, 12px);
  background: radial-gradient(circle at 40% 35%, #fffbe8 0%, #f6c760 45%, #d99a12 100%);
  clip-path: polygon(50% 0, 58% 42%, 100% 50%, 58% 58%, 50% 100%, 42% 58%, 0 50%, 42% 42%);
  filter: drop-shadow(0 0 5px rgba(214, 160, 20, 0.9)) drop-shadow(0 1px 1px rgba(90, 60, 0, 0.5));
  opacity: 0;
  will-change: transform, opacity;
  animation: offer-spark-twinkle var(--dur, 3.4s) ease-in-out infinite;
  animation-delay: var(--delay, 0s);
}

@keyframes offer-spark-twinkle {
  0%,
  100% {
    opacity: 0;
    transform: translateY(6px) scale(0.2) rotate(0deg);
  }

  40% {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(18deg);
  }

  72% {
    opacity: 0.85;
    transform: translateY(-5px) scale(0.92) rotate(28deg);
  }
}

.offer-celebrate-loop--paused .offer-celebrate-spark {
  animation-play-state: paused;
}

@media (prefers-reduced-motion: reduce) {
  .offer-celebrate-loop {
    display: none;
  }
}

/* ── Festival Combo (P3-A) — the always-on "party" skin layered over the twinkle
   field on completed cards: a soft gold glow that breathes, a continuous festival
   confetti rain, and a light sheen sweep. All composite-only (transform/opacity),
   deterministic, pausable off-screen, fully suppressed under reduced-motion. ─── */

/* Warm gold glow — opacity-only pulse of a static radial wash (NO box-shadow
   animation, which would trigger paint on every completed card in the list). */
.offer-celebrate-glow {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: radial-gradient(120% 82% at 50% 0%, rgba(246, 199, 96, 0.45), transparent 60%);
  opacity: 0.25;
  will-change: opacity;
  animation: offer-glow-pulse 2.6s ease-in-out infinite;
}

.offer-celebrate-glow--paused {
  animation-play-state: paused;
}

@keyframes offer-glow-pulse {
  0%,
  100% {
    opacity: 0.22;
  }

  50% {
    opacity: 0.6;
  }
}

/* Confetti rain — festival-colored pieces falling through the media, clipped by
   the media's overflow. Between the glow (z2) and the sheen (z5). */
.offer-celebrate-confetti {
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  overflow: hidden;
}

.offer-celebrate-confetti-piece {
  position: absolute;
  top: -16%;
  width: var(--w, 7px);
  height: calc(var(--w, 7px) * 1.4);
  border-radius: 1px;
  opacity: 0;
  will-change: transform, opacity;
  animation: offer-confetti-fall var(--dur, 3.4s) linear infinite;
  animation-delay: var(--delay, 0s);
}

.offer-celebrate-confetti--paused .offer-celebrate-confetti-piece {
  animation-play-state: paused;
}

@keyframes offer-confetti-fall {
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 0;
  }

  12% {
    opacity: 1;
  }

  88% {
    opacity: 1;
  }

  100% {
    transform: translateY(230px) rotate(560deg);
    opacity: 0;
  }
}

/* Light sheen — a soft diagonal gloss sweeping across the whole media on loop.
   Symmetric skew/translate (cosmetic), so no RTL override is needed. */
.offer-celebrate-sheen {
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
  overflow: hidden;
}

.offer-celebrate-sheen-bar {
  position: absolute;
  top: -20%;
  bottom: -20%;
  width: 42%;
  background: linear-gradient(105deg, transparent, rgba(255, 251, 232, 0.5), transparent);
  transform: translateX(-160%) skewX(-18deg);
  will-change: transform;
  animation: offer-sheen-sweep 3.6s ease-in-out infinite;
}

.offer-celebrate-sheen--paused .offer-celebrate-sheen-bar {
  animation-play-state: paused;
}

@keyframes offer-sheen-sweep {
  0% {
    transform: translateX(-160%) skewX(-18deg);
  }

  55% {
    transform: translateX(320%) skewX(-18deg);
  }

  100% {
    transform: translateX(320%) skewX(-18deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .offer-celebrate-confetti,
  .offer-celebrate-sheen {
    display: none;
  }

  .offer-celebrate-glow {
    animation: none;
    opacity: 0.32;
  }
}

/* ── Completion banner — one-time, self-dismissing, centered over media.
   Reduced-motion falls back to a plain opacity fade (grammar copied from
   PoolaFestivalOpening.vue). ─────────────────────────────────────────────── */
.offer-celebrate-banner {
  position: absolute;
  inset-inline: var(--poolit-spacing-md, 16px);
  bottom: var(--poolit-spacing-md, 16px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 14px;
  border-radius: var(--poolit-radius-md, 8px);
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  text-align: center;
  backdrop-filter: blur(4px);
  animation: offer-celebrate-banner-in 0.4s cubic-bezier(0.2, 0.7, 0.3, 1);
}

.offer-celebrate-banner__title {
  font-size: var(--poolit-font-size-body, 0.9375rem);
  font-weight: var(--poolit-font-weight-bold, 700);
}

.offer-celebrate-banner__reward {
  font-size: 12px;
  opacity: 0.9;
}

@keyframes offer-celebrate-banner-in {
  from {
    transform: translateY(6px) scale(0.97);
    opacity: 0;
  }

  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

.offer-celebrate-banner[data-reduced='true'] {
  animation: offer-celebrate-banner-fade 0.4s ease;
}

@keyframes offer-celebrate-banner-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .offer-celebrate-banner {
    animation: offer-celebrate-banner-fade 0.4s ease;
  }
}

.offer-overlay--mine {
  inset-inline-end: var(--poolit-spacing-sm, 8px);
  bottom: var(--poolit-spacing-sm, 8px);
  background: var(--poolit-primary-500, #8b5cf6);
  color: #fff;
}

.offer-overlay--supplier {
  inset-inline-start: var(--poolit-spacing-sm, 8px);
  bottom: var(--poolit-spacing-sm, 8px);
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  padding: 5px 10px;
  backdrop-filter: blur(4px);
  max-width: calc(100% - 32px);
}

.supplier-chip__avatar-wrap {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.supplier-chip__avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.supplier-chip__name {
  font-size: 12px;
  font-weight: var(--poolit-font-weight-medium, 500);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supplier-chip__vetted {
  flex-shrink: 0;
}

/* ── Card content ────────────────────────────────────────────────────────── */
.offer-title {
  font-size: var(--poolit-font-size-lg, 1.125rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  margin: 0 0 4px;
  line-height: 1.35;
}

.offer-catalog {
  font-size: var(--poolit-font-size-small, 0.8125rem);
  color: var(--f7-text-color-secondary, #6b7280);
  margin: 0 0 var(--poolit-spacing-sm, 8px);
}

.offer-flavor {
  font-size: var(--poolit-font-size-small, 0.8125rem);
  color: var(--f7-text-color-secondary, #6b7280);
  margin: 0 0 var(--poolit-spacing-sm, 8px);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

/* ── Cash-hero price (D2 · Treatment 1) — the cash you pay is the hero number;
   retail is a small struck reference; the PoolShare-covered amount is the
   members' benefit (PoolShare-powered, not a store discount) ─────────────────── */
.price-split {
  margin-block: var(--poolit-spacing-sm, 8px);
}

.price-cash__label {
  font-size: 12px;
  color: var(--f7-text-color-secondary, #6b7280);
  margin: 0;
}

.price-cash__hero {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 1.875rem;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--f7-text-color, #111827);
  line-height: 1.1;
  margin: 2px 0 0;
}

.price-cash__retail {
  font-size: 13px;
  font-weight: var(--poolit-font-weight-regular, 400);
  color: var(--poolit-neutral-400, #9ca3af);
}

.price-cash__covers {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: var(--poolit-font-weight-medium, 500);
  color: var(--poolit-primary-700, #6d28d9);
  background: var(--poolit-primary-50, #f5f3ff);
  border-radius: var(--poolit-radius-md, 8px);
  padding: 7px 11px;
  margin: 10px 0 0;
}

/* dir="ltr" set inline on the element — keeps cash-left, poolshare-right
   as a data visualisation even when the page is RTL. No :global() needed. */
.price-split__bar {
  display: flex;
  height: 8px;
  border-radius: var(--poolit-radius-full);
  overflow: hidden;
  gap: 2px;
  margin-top: 10px;
}

.price-split__segment {
  border-radius: var(--poolit-radius-full);
  min-width: 4px;
  transition: flex 0.3s ease;
}

.price-split__segment--cash {
  background: var(--poolit-neutral-500, #6b7280);
}

.price-split__segment--poolshare {
  background: var(--poolit-primary-500, #8b5cf6);
}

.price-split__legend {
  font-size: 12px;
  color: var(--f7-text-color-secondary, #6b7280);
  margin: 6px 0 0;
}

/* ── Progress + social proof ─────────────────────────────────────────────── */
.offer-progress {
  margin-block: var(--poolit-spacing-sm, 8px);
}

.offer-progress__text {
  font-size: 12px;
  color: var(--f7-text-color-secondary, #6b7280);
  margin: 4px 0 0;
}

/* ── Cap chip ─────────────────────────────────────────────────────────────── */
.offer-cap-chip {
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: var(--poolit-radius-full);
  margin-block-end: var(--poolit-spacing-sm, 8px);
  color: var(--f7-text-color, #111827);
}

/* Tappable variant of the cap chip — resets native <button> styling so it
   reads as the same pill, not a browser button. */
.offer-promise-chip {
  border: none;
  cursor: pointer;
  font: inherit;
  color: var(--poolit-primary-700, #6d28d9);
  background: var(--poolit-primary-50, #f5f3ff);
}

/* ── CTA row ──────────────────────────────────────────────────────────────── */
.offer-cta-row {
  display: flex;
  align-items: center;
  gap: var(--poolit-spacing-sm, 8px);
  margin-block-start: var(--poolit-spacing-sm, 8px);
}

.offer-cta-btn {
  flex: 1;
}

/* Cap-unlock in-progress CTA (P2-A) — the button is disabled BECAUSE the member
   is at their per-member cap and a lift is counting down. Accent-skinned (NOT the
   plain grey disabled look) so it reads "unlocking soon", not dead. Grows to two
   lines (copy + HH:MM:SS). Symmetric styling → no RTL override needed. */
.offer-cta-btn--cap-unlock {
  background: var(--poolit-primary-600, #7a3ef0) !important;
  color: #fff !important;
  height: auto !important;
  min-height: var(--f7-button-large-height, 48px);
  padding-block: 7px;
  box-shadow: inset 0 -3px 0 var(--poolit-accent-500, #23ecf6);
}

/* F7 dims `.disabled` to opacity 0.55 !important — override back to 1 so the
   accent skin stays legible. `pointer-events: none` from F7's rule still blocks
   the click, so the button remains genuinely disabled (enabling happens ONLY via
   the P2-A refetch flipping the server `canClaim`, never this timer). */
.offer-cta-btn--cap-unlock.disabled,
.offer-cta-btn--cap-unlock:disabled {
  opacity: 1 !important;
}

.cap-unlock-cta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.2;
}

.cap-unlock-cta__line1 {
  font-size: 13px;
  font-weight: var(--poolit-font-weight-semibold, 600);
}

.cap-unlock-cta__time {
  font-size: 15px;
  font-weight: var(--poolit-font-weight-bold, 700);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
}

/* ── Completed reward / social row (replaces the CTA row) ─────────────────── */
.offer-completed-row {
  margin-block-start: var(--poolit-spacing-sm, 8px);
}

.offer-reward-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: var(--poolit-font-weight-semibold, 600);
  color: #92700c;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-radius: var(--poolit-radius-md, 8px);
  padding: 9px 12px;
}

.offer-reward-chip--social {
  color: var(--f7-text-color-secondary, #6b7280);
  background: rgba(0, 0, 0, 0.06);
}

.offer-source-link {
  font-size: var(--poolit-font-size-small, 0.8125rem);
  white-space: nowrap;
}

/* ── Members-only tag (prospect read-only) ────────────────────────────────── */
.offer-members-only {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-block-start: var(--poolit-spacing-sm, 8px);
  padding: 7px 12px;
  border-radius: var(--poolit-radius-full);
  background: var(--poolit-primary-50, #f5f3ff);
  color: var(--poolit-primary-700, #6d28d9);
  font-size: 13px;
  font-weight: var(--poolit-font-weight-semibold, 600);
}

.offer-members-only__icon {
  flex-shrink: 0;
}

/* ── Share action slots ───────────────────────────────────────────────────── */
.offer-share--primary {
  margin-block-end: var(--poolit-spacing-sm, 8px);
}

.offer-share--secondary {
  margin-block-start: var(--poolit-spacing-sm, 8px);
}
</style>
